import { ModalTypes } from 'enums/modalTypes';
import {
  StageCandidateFindManyDocument,
  StageCandidateStatus,
  useDeiStageActionCandidateReopenMutation,
  useDeiStageActionCandidateRevokeConsentMutation,
  useResetCoderbyteAssessmentMutation,
  useResetCognitiveTestMutation,
  useResetEnglishProficiencyAssessmentMutation,
  useResetPersonalityTestMutation,
  useRssAssessmentCandidateReopenAllSubmittedForStageCandidateMutation,
  useStageCandidateReprocessOneMutation,
  useStageCandidateResetOneMutation,
} from 'generated/graphql';
import { closeModal, openModal } from 'graphql/modals';
import { FC, useMemo, useState } from 'react';
import {
  Button,
  NotificationModal,
  NotificationModalType,
  Table,
  TNotification,
  Tooltip,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
  IActionsResultTable,
  IFilteredTestActionCandidate,
  TestActionType,
} from '../UserResets.interface';
import styles from './ActionsResultTable.module.scss';
import {
  convertToRows,
  getTestActionName,
  ModalContent,
} from './ActionsTable.helpers';
import { headerData, unsortableHeaderKeys } from './headerData.constant';

const ActionsResultTable: FC<IActionsResultTable> = ({
  userInfo,
  stageStatus,
  filteredTestActionCandidates,
  projectName,
  rssAssessmentCandidates,
  stageCandidateId,
  rsForm,
  deiForm,
}) => {
  const { handleMsgType } = useNotification();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalInfo, setModalInfo] = useState<{
    type: keyof typeof ModalContent;
    actionCandidateId?: string;
    testActionType?: TestActionType;
    testType?: string;
    projectName?: string;
  }>({
    type: 'resetStage',
    actionCandidateId: '',
    testType: '',
    projectName: projectName,
  });

  const [resetOneCandidateStageMutation, { loading: isResetStageLoading }] =
    useStageCandidateResetOneMutation({
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          message: error?.message,
          title: 'Something went wrong',
          dismissable: true,
        });
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'Stage reset successfully',
          dismissable: true,
        });
        setIsModalOpen(false);
      },
      refetchQueries: [
        {
          query: StageCandidateFindManyDocument,
          variables: { candidateId: userInfo?.id },
        },
      ],
    });

  const resetTestMutationOptions = {
    onError: (error: { message?: string }) => {
      handleMsgType({
        type: TNotification.error,
        message: error?.message,
        title: 'Something went wrong',
        dismissable: true,
      });
    },
    onCompleted: () => {
      handleMsgType({
        type: TNotification.success,
        title: 'Test reset successfully',
        dismissable: true,
      });
      setIsModalOpen(false);
    },
    refetchQueries: [
      {
        query: StageCandidateFindManyDocument,
        variables: { candidateId: userInfo?.id },
      },
    ],
  };

  const [resetCognitiveTestMutation, { loading: isResetCognitiveTestLoading }] =
    useResetCognitiveTestMutation(resetTestMutationOptions);

  const [
    resetPersonalityTestMutation,
    { loading: isResetPersonalityTestLoading },
  ] = useResetPersonalityTestMutation(resetTestMutationOptions);

  const [resetCoderbyteMutation, { loading: isResetCoderbyteLoading }] =
    useResetCoderbyteAssessmentMutation(resetTestMutationOptions);

  const [
    resetEnglishProficiencyMutation,
    { loading: isResetEnglishProficiencyLoading },
  ] = useResetEnglishProficiencyAssessmentMutation(resetTestMutationOptions);

  const isResetTestLoading =
    isResetCognitiveTestLoading ||
    isResetPersonalityTestLoading ||
    isResetCoderbyteLoading ||
    isResetEnglishProficiencyLoading;

  const [
    rssAssessmentCandidateReOpenMutation,
    { loading: isRssAssessmentCandidateReOpenLoading },
  ] = useRssAssessmentCandidateReopenAllSubmittedForStageCandidateMutation({
    variables: {
      stageCandidateId,
    },
    onCompleted: () => {
      handleMsgType({
        type: TNotification.success,
        title: 'Skills self assessment reopened!',
        dismissable: true,
      });
    },
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        message: error?.message,
        title: 'Something went wrong',
        dismissable: true,
      });
    },
  });

  const [reopenForm, { loading }] = useDeiStageActionCandidateReopenMutation({
    onCompleted: () => {
      handleMsgType({
        type: TNotification.success,
        title: 'Demographic information questionnaire successfully reopened',
      });
      closeModal(ModalTypes.NOTIFICATION_MODAL);
    },
    onError: () => {
      handleMsgType({
        type: TNotification.error,
        title: 'Demographic information questionnaire could not be reopened',
        message: 'Something went wrong. Please try again.',
      });
      closeModal(ModalTypes.NOTIFICATION_MODAL);
    },
  });

  const [revokeConsent, { loading: isRevokeConsentLoading }] =
    useDeiStageActionCandidateRevokeConsentMutation({
      refetchQueries: [
        {
          query: StageCandidateFindManyDocument,
          variables: { candidateId: userInfo?.id },
        },
      ],
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'Consent revoked and demographic information erased',
        });
        closeModal(ModalTypes.NOTIFICATION_MODAL);
      },
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          title: 'Consent could not be revoked',
          message: error?.message,
        });
        closeModal(ModalTypes.NOTIFICATION_MODAL);
      },
    });

  const [recalculateScoresMutation, { loading: isRecalculateScoresLoading }] =
    useStageCandidateReprocessOneMutation({
      variables: {
        id: stageCandidateId,
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'Scores recalculated successfully',
        });
      },
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          title: 'Something went wrong',
          message: error?.message,
        });
      },
    });

  const handleResetDeiModal = () => {
    const modalProps = {
      title: 'Are you sure?',
      message:
        'This action will reopen the questionnaire to the user, without deleting previous responses.',
      confirmButtonProps: { loading: loading },
      onOkClick: () => {
        reopenForm({ variables: { stageCandidateId } });
      },
    };
    openModal(ModalTypes.NOTIFICATION_MODAL, modalProps);
  };

  const handleRevokeConsentModal = () => {
    openModal(ModalTypes.NOTIFICATION_MODAL, {
      title: 'Revoke consent?',
      message:
        "This erases the candidate's demographic information answers for this role and records the withdrawal of consent. This cannot be undone.",
      okButtonChildren: 'Revoke consent',
      confirmButtonProps: {
        variant: 'negativePrimary',
        loading: isRevokeConsentLoading,
      },
      onOkClick: () => {
        revokeConsent({ variables: { stageCandidateId } });
      },
    });
  };

  const handleResetTest = () => {
    if (!modalInfo?.actionCandidateId) return;
    const variables = { id: modalInfo.actionCandidateId };
    switch (modalInfo.testActionType) {
      case TestActionType.COGNITIVE:
        resetCognitiveTestMutation({ variables });
        break;
      case TestActionType.PERSONALITY:
        resetPersonalityTestMutation({ variables });
        break;
      case TestActionType.CODERBYTE:
        resetCoderbyteMutation({ variables });
        break;
      case TestActionType.ENGLISH_PROFICIENCY:
        resetEnglishProficiencyMutation({ variables });
        break;
    }
  };

  const handleMitigateCircumstance = (
    isDefault: boolean,
    item: IFilteredTestActionCandidate
  ) => {
    openModal(ModalTypes.MITIGATE_CIRCUMSTANCE_MODAL, {
      testActionCandidate: item,
      isDefault,
      candidateId: userInfo?.id || 0,
    });
  };

  const handleResetStage = () => {
    resetOneCandidateStageMutation({ variables: { id: stageCandidateId } });
  };

  const handleRssAssessmentCandidateReOpen = () => {
    rssAssessmentCandidateReOpenMutation();
  };

  const handleResetStageOnClick = () => {
    setModalInfo({
      type: 'resetStage',
    });

    setIsModalOpen(true);
  };

  const handleRecalculateScoresOnClick = () => {
    recalculateScoresMutation();
  };

  const handleResetTestOnClick = (item: IFilteredTestActionCandidate) => {
    setModalInfo({
      type: 'resetTest',
      actionCandidateId: item.id,
      testActionType: item.type,
      testType: getTestActionName(item),
      projectName: projectName,
    });
    setIsModalOpen(true);
  };

  const isResetStageNotCompleted =
    stageStatus !== StageCandidateStatus.Completed;

  const isResetLoading =
    modalInfo.type === 'resetStage' ? isResetStageLoading : isResetTestLoading;

  const rows = useMemo(
    () =>
      convertToRows(
        filteredTestActionCandidates,
        rssAssessmentCandidates,
        handleMitigateCircumstance,
        handleResetTestOnClick,
        isRssAssessmentCandidateReOpenLoading,
        handleRssAssessmentCandidateReOpen,
        handleResetDeiModal,
        handleRevokeConsentModal,
        rsForm,
        deiForm
      ),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredTestActionCandidates, rssAssessmentCandidates, projectName]
  );

  return (
    <div className={styles.tableContainer}>
      <Table
        headerData={headerData}
        rowData={rows}
        unsortableHeaderKeys={unsortableHeaderKeys}
        className={styles.table}
        hasPagination={false}
        hasToolbar
        toolbarConf={{
          componentListLeft: (
            <Tooltip
              content={
                isResetStageNotCompleted
                  ? 'Cannot recalculate scores until all tests are completed'
                  : 'Recalculate soft skill scores then create and email reports'
              }
            >
              <Button
                size="medium"
                variant="secondary"
                className={styles.recalculateButton}
                onClick={handleRecalculateScoresOnClick}
                disabled={isResetStageNotCompleted}
                loading={isRecalculateScoresLoading}
              >
                Recalculate scores
              </Button>
            </Tooltip>
          ),
          componentListRight: (
            <Button
              size="medium"
              variant="primary"
              className={styles.resetStageButton}
              onClick={handleResetStageOnClick}
              loading={isResetStageLoading}
              disabled={isResetStageLoading}
            >
              Reset stage
            </Button>
          ),
        }}
      />
      {isModalOpen && (
        <NotificationModal
          isOpen
          onOkClick={
            modalInfo.type === 'resetStage' ? handleResetStage : handleResetTest
          }
          confirmButtonProps={{
            variant: 'negativePrimary',
            loading: isResetLoading,
            disabled: isResetLoading,
          }}
          okButtonChildren={`Reset ${
            ModalContent[modalInfo.type].okButtonChildren
          }`}
          onClose={() => setIsModalOpen(false)}
          title="Are you sure?"
          type={NotificationModalType.WARNING}
          message={
            modalInfo.type === 'resetTest'
              ? ModalContent[modalInfo.type].message(
                  modalInfo?.projectName || '',
                  modalInfo?.testType || ''
                )
              : ModalContent[modalInfo.type].message
          }
        />
      )}
    </div>
  );
};

export default ActionsResultTable;
